import * as React from 'react'
import { NavigationType, useBlocker, type BlockerFunction } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { useT } from '@/components/visitone/visitone-provider'

/*
 * The shared unsaved-changes capability (VIS-321). Every configuration form
 * registers its unsaved changes here, and every way of leaving them goes
 * through one guard: in-app navigation and browser back (the one router
 * blocker), closing a slide-over, logging out, switching tenant, Discard, and
 * — through the browser's own prompt — refresh, tab close and external links.
 * With nothing unsaved the action goes ahead without asking. Otherwise one
 * dialog offers Keep editing or Discard and continue.
 *
 * This file is the only place allowed to use `useBlocker` or listen for
 * `beforeunload` (ESLint enforces it): a router supports one blocker at a time.
 */

export type UnsavedAction =
  | { kind: 'leave' }
  | { kind: 'close' }
  | { kind: 'logout' }
  | { kind: 'switch'; tenant: string }
  | { kind: 'discard' }

export type OtherLanguageChanges = { language: string; count: number }

export type UnsavedChanges = {
  /** Fields whose value differs from the saved value. */
  count: number
  /** What the changes are on, e.g. the order type's name. */
  subject: string
  /** Changes in content languages other than the one on screen. */
  otherLanguages?: OtherLanguageChanges[]
  /** Puts every field back to its saved value. */
  discard: () => void
}

type Entry = UnsavedChanges & { order: number }

type Summary = { count: number; subject: string; otherLanguages: OtherLanguageChanges[] }

type Pending = Summary & {
  action: UnsavedAction
  proceed: () => void
  stay?: () => void
  returnFocus: HTMLElement | null
  confirmFocus: HTMLElement | null
}

export type ConfirmOptions = {
  /** Where focus returns on Keep editing; defaults to the focused element. */
  returnFocus?: HTMLElement | null
  /** Where focus goes once confirmed, for an action that keeps the user on the page, such as Discard. */
  confirmFocus?: HTMLElement | null
}

type GuardValue = {
  register: (id: string, changes: UnsavedChanges | null) => void
  hasUnsavedChanges: () => boolean
  confirmThen: (action: UnsavedAction, proceed: () => void, options?: ConfirmOptions) => void
}

const GuardContext = React.createContext<GuardValue | null>(null)

function focusedElement() {
  return document.activeElement instanceof HTMLElement && document.activeElement !== document.body
    ? document.activeElement
    : null
}

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const entries = React.useRef(new Map<string, Entry>())
  const nextOrder = React.useRef(0)
  const pendingRef = React.useRef<Pending | null>(null)
  const focusAfterClose = React.useRef<HTMLElement | null>(null)
  const [pending, setPendingState] = React.useState<Pending | null>(null)

  const setPending = React.useCallback((next: Pending | null) => {
    pendingRef.current = next
    setPendingState(next)
  }, [])

  const hasUnsavedChanges = React.useCallback(() => entries.current.size > 0, [])

  const register = React.useCallback((id: string, changes: UnsavedChanges | null) => {
    const map = entries.current
    if (!changes || changes.count <= 0) {
      map.delete(id)
      return
    }
    map.set(id, { ...changes, order: map.get(id)?.order ?? ++nextOrder.current })
    if (import.meta.env.DEV && map.size > 1) {
      console.warn('VIS-321: more than one form has unsaved changes; only one should at a time.')
    }
  }, [])

  const summarise = React.useCallback((): Summary => {
    const list = [...entries.current.values()].sort((a, b) => a.order - b.order)
    const otherLanguages = new Map<string, number>()
    for (const entry of list) {
      for (const other of entry.otherLanguages ?? []) {
        otherLanguages.set(other.language, (otherLanguages.get(other.language) ?? 0) + other.count)
      }
    }
    return {
      count: list.reduce((sum, entry) => sum + entry.count, 0),
      subject: list.at(-1)?.subject ?? '',
      otherLanguages: [...otherLanguages].map(([language, count]) => ({ language, count })),
    }
  }, [])

  const ask = React.useCallback(
    (action: UnsavedAction, proceed: () => void, options: ConfirmOptions, stay?: () => void) => {
      if (pendingRef.current) return
      setPending({
        ...summarise(),
        action,
        proceed,
        stay,
        returnFocus: options.returnFocus ?? focusedElement(),
        confirmFocus: options.confirmFocus ?? null,
      })
    },
    [setPending, summarise],
  )

  const confirmThen = React.useCallback(
    (action: UnsavedAction, proceed: () => void, options: ConfirmOptions = {}) => {
      if (!hasUnsavedChanges()) {
        proceed()
        return
      }
      ask(action, proceed, options)
    },
    [ask, hasUnsavedChanges],
  )

  const shouldBlock = React.useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) =>
      hasUnsavedChanges() &&
      (currentLocation.pathname !== nextLocation.pathname || historyAction === NavigationType.Pop),
    [hasUnsavedChanges],
  )
  const blocker = useBlocker(shouldBlock)

  React.useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (pendingRef.current) {
      blocker.reset()
      return
    }
    ask(
      { kind: 'leave' },
      () => blocker.proceed(),
      {},
      () => blocker.reset(),
    )
  }, [blocker, ask])

  React.useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return
      event.preventDefault()
      // Older browsers show their prompt only when returnValue is set.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasUnsavedChanges])

  const stay = React.useCallback(() => {
    const current = pendingRef.current
    if (!current) return
    focusAfterClose.current = current.returnFocus
    setPending(null)
    current.stay?.()
  }, [setPending])

  const confirm = React.useCallback(() => {
    const current = pendingRef.current
    if (!current) return
    focusAfterClose.current = current.confirmFocus
    setPending(null)
    // Cleared synchronously so the action itself (a navigation, or the
    // sign-out redirect) is never asked about a second time.
    const discarded = [...entries.current.values()]
    entries.current.clear()
    for (const entry of discarded) entry.discard()
    current.proceed()
  }, [setPending])

  // Runs after the dialog's own effect has closed it.
  React.useEffect(() => {
    if (pending) return
    const target = focusAfterClose.current
    focusAfterClose.current = null
    if (target?.isConnected) target.focus()
  }, [pending])

  const value = React.useMemo<GuardValue>(
    () => ({ register, hasUnsavedChanges, confirmThen }),
    [register, hasUnsavedChanges, confirmThen],
  )

  return (
    <GuardContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog pending={pending} onStay={stay} onConfirm={confirm} />
    </GuardContext.Provider>
  )
}

function useGuard() {
  const context = React.useContext(GuardContext)
  if (!context) throw new Error('Unsaved changes are only available inside an UnsavedChangesProvider')
  return context
}

/** Asks before an action that would discard unsaved changes; runs it at once when there are none. */
export function useUnsavedChanges() {
  const { confirmThen, hasUnsavedChanges } = useGuard()
  return { confirmThen, hasUnsavedChanges }
}

/**
 * Registers a form's unsaved changes with the guard while `changes` is set
 * and its count is above 0. A form that cannot be edited never registers.
 */
export function useRegisterUnsavedChanges(changes: UnsavedChanges | null) {
  const { register } = useGuard()
  const id = React.useId()
  const discard = React.useRef(changes?.discard)
  React.useLayoutEffect(() => {
    discard.current = changes?.discard
  })

  const count = changes?.count ?? 0
  const subject = changes?.subject ?? ''
  const otherLanguages = JSON.stringify(changes?.otherLanguages ?? [])

  React.useLayoutEffect(() => {
    register(
      id,
      count > 0
        ? {
            count,
            subject,
            otherLanguages: JSON.parse(otherLanguages) as OtherLanguageChanges[],
            discard: () => discard.current?.(),
          }
        : null,
    )
  }, [id, register, count, subject, otherLanguages])

  React.useLayoutEffect(() => () => register(id, null), [id, register])
}

function UnsavedChangesDialog({
  pending,
  onStay,
  onConfirm,
}: {
  pending: Pending | null
  onStay: () => void
  onConfirm: () => void
}) {
  const t = useT()
  const titleId = React.useId()
  const bodyId = React.useId()

  const consequence = (action: UnsavedAction) =>
    action.kind === 'switch'
      ? t('forms.dialog.switch.body', { tenant: action.tenant })
      : t(`forms.dialog.${action.kind}.body`)

  return (
    <Modal open={Boolean(pending)} onClose={onStay} labelledBy={titleId} describedBy={bodyId}>
      {pending ? (
        <>
          <h2 id={titleId} className="text-base font-semibold text-fg-strong">
            {t('forms.dialog.title')}
          </h2>
          <div id={bodyId} className="mt-2 flex flex-col gap-1 text-sm text-fg-muted">
            <p>{t('forms.dialog.count', { count: pending.count, subject: pending.subject })}</p>
            {pending.otherLanguages.map((other) => (
              <p key={other.language}>
                {t('forms.dialog.otherLanguage', { count: other.count, language: other.language })}
              </p>
            ))}
            <p>{consequence(pending.action)}</p>
          </div>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button variant="secondary" size="lg" autoFocus onClick={onStay}>
              {t('forms.dialog.keepEditing')}
            </Button>
            <Button variant="destructive" size="lg" onClick={onConfirm}>
              {t(`forms.dialog.${pending.action.kind}.confirm`)}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  )
}
